import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import SectionTitle from '@/components/base/SectionTitle';
import { categories } from '@/mocks/categories';
import { getFeaturedComics, getPopularComics, getLatestComics, comics } from '@/mocks/comics';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const featured = getFeaturedComics();

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % featured.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + featured.length) % featured.length);

  const popular = getPopularComics(8);
  const latest = getLatestComics(8);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20illustration%20of%20a%20cozy%20reading%20nook%20with%20floating%20comic%20panels%20and%20book%20pages%20swirling%20in%20warm%20golden%20amber%20light%2C%20soft%20magical%20atmosphere%2C%20warm%20color%20palette%20of%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20shapes%2C%20dreamy%20artistic%20style%2C%20no%20text&width=1920&height=1080&seq=hero-bg-home&orientation=landscape"
            alt="非麻瓜漫画 - 免费韩漫在线阅读"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70"></div>

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-4 leading-tight">
              非麻瓜漫画
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
              海量<strong>免费韩漫</strong>在线观看，支持<strong>下拉式阅读</strong>体验
              <br className="hidden sm:block" />
              每日更新最新<strong>韩国漫画全集</strong>，沉浸式漫画阅读尽在非麻瓜漫画
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/romance"
                className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-white/30 text-white font-medium rounded-full hover:bg-white/10 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Carousel */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 -mt-20 relative z-20">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-6">
            <SectionTitle title="精选推荐" subtitle="本周最热门的韩漫推荐" />
            <div className="relative">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {featured.map((comic) => (
                    <div key={comic.id} className="w-full shrink-0">
                      <div className="flex flex-col md:flex-row gap-6">
                        <Link to={`/comic/${comic.id}`} className="shrink-0 w-full md:w-[200px] h-[280px] rounded-lg overflow-hidden cursor-pointer">
                          <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                        </Link>
                        <div className="flex flex-col justify-center">
                          <Link to={`/comic/${comic.id}`} className="cursor-pointer">
                            <h3 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading hover:text-primary-500 transition-colors">{comic.title}</h3>
                          </Link>
                          <p className="text-sm text-foreground-600 mt-1">{comic.author} · {comic.status}</p>
                          <p className="text-sm text-foreground-700 mt-3 leading-relaxed line-clamp-3">{comic.description}</p>
                          <div className="flex flex-wrap items-center gap-3 mt-4">
                            <span className="flex items-center gap-1 text-accent-500 text-sm">
                              <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                              {comic.rating}
                            </span>
                            <span className="text-sm text-foreground-600">{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读</span>
                            <span className="text-sm text-foreground-600">更新至 {comic.chapters.length} 话</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {comic.tags.map((tag) => (
                              <span key={tag} className="text-xs text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded whitespace-nowrap">{tag}</span>
                            ))}
                          </div>
                          <Link
                            to={`/comic/${comic.id}`}
                            className="mt-4 self-start px-6 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                          >
                            立即阅读
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 w-9 h-9 md:w-10 md:h-10 bg-background-50 border border-background-200/70 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                aria-label="上一个"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 w-9 h-9 md:w-10 md:h-10 bg-background-50 border border-background-200/70 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                aria-label="下一个"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>

              <div className="flex justify-center gap-2 mt-4">
                {featured.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                      idx === currentSlide ? 'bg-primary-500 w-5' : 'bg-background-300 hover:bg-background-400'
                    }`}
                    aria-label={`切换到第 ${idx + 1} 张`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 mt-12">
          <SectionTitle title="漫画分类" subtitle="探索你喜欢的韩漫类型" />
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-background-100 transition-all duration-300 cursor-pointer group"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-lg`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Popular Comics */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 mt-14">
          <SectionTitle
            title="热门排行"
            subtitle="最受读者欢迎的韩漫"
            action={
              <Link to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap">
                查看更多 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {popular.map((comic, idx) => (
              <div key={comic.id} className="relative">
                <span className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                  {idx + 1}
                </span>
                <ComicCard comic={comic} />
              </div>
            ))}
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 mt-14 mb-14">
          <SectionTitle
            title="最新更新"
            subtitle="每日持续更新中"
            action={
              <Link to="/category/fantasy" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap">
                查看更多 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {latest.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20artistic%20illustration%20with%20floating%20colorful%20manga%20pages%20and%20soft%20glowing%20particles%2C%20golden%20amber%20and%20coral%20toned%20abstract%20background%2C%20dreamy%20ethereal%20atmosphere%2C%20warm%20light%20rays%2C%20modern%20artistic%20style%2C%20no%20text&width=1920&height=600&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-4">
              开启你的韩漫阅读之旅
            </h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              非麻瓜漫画汇聚海量优质韩漫资源，<strong>免费韩漫</strong>随时随地畅读。
              支持<strong>下拉式漫画</strong>阅读体验，沉浸式享受<strong>韩国漫画</strong>的精彩世界。
            </p>
            <Link
              to="/category/romance"
              className="inline-block px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              立即免费阅读
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-14">
          <SectionTitle title="常见问题" subtitle="关于非麻瓜漫画的常见疑问" />
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              { q: '非麻瓜漫画需要注册吗？', a: '不需要！非麻瓜漫画所有韩漫资源均可免费在线阅读，无需注册账号，打开网站即可享受沉浸式漫画阅读体验。' },
              { q: '什么是下拉式漫画阅读？', a: '下拉式漫画是韩国漫画的特色阅读方式，您只需向下滑动页面即可连续阅读，无需翻页，带来流畅的阅读体验。' },
              { q: '漫画多久更新一次？', a: '非麻瓜漫画每日更新最新韩国漫画全集，热门连载作品保持与韩国同步更新频率，让您第一时间阅读到最新内容。' },
              { q: '可以在手机上阅读吗？', a: '当然可以！非麻瓜漫画完美适配手机端阅读，支持下拉式漫画浏览，在手机上也能获得出色的韩漫阅读体验。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                  <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "非麻瓜漫画 - 免费韩漫在线阅读",
          "description": "非麻瓜漫画提供海量免费韩漫在线观看，支持下拉式阅读体验，每日更新最新韩国漫画全集。",
          "url": "https://feimagua.com",
          "inLanguage": "zh-Hans",
          "isPartOf": { "@type": "WebSite", "name": "非麻瓜漫画", "url": "https://feimagua.com" }
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "非麻瓜漫画需要注册吗？", "acceptedAnswer": { "@type": "Answer", "text": "不需要！非麻瓜漫画所有韩漫资源均可免费在线阅读，无需注册账号，打开网站即可享受沉浸式漫画阅读体验。" } },
            { "@type": "Question", "name": "什么是下拉式漫画阅读？", "acceptedAnswer": { "@type": "Answer", "text": "下拉式漫画是韩国漫画的特色阅读方式，您只需向下滑动页面即可连续阅读，无需翻页，带来流畅的阅读体验。" } },
            { "@type": "Question", "name": "漫画多久更新一次？", "acceptedAnswer": { "@type": "Answer", "text": "非麻瓜漫画每日更新最新韩国漫画全集，热门连载作品保持与韩国同步更新频率，让您第一时间阅读到最新内容。" } },
            { "@type": "Question", "name": "可以在手机上阅读吗？", "acceptedAnswer": { "@type": "Answer", "text": "当然可以！非麻瓜漫画完美适配手机端阅读，支持下拉式漫画浏览，在手机上也能获得出色的韩漫阅读体验。" } }
          ]
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://feimagua.com" }
          ]
        })
      }} />
    </>
  );
}